import { FC } from "react";
import {
  ChevronDownIcon,
  ChevronUpIcon,
  CheckIcon,
  SparklesIcon,
} from "@heroicons/react/24/outline";
import { classNames } from "@via/utils/helpers";
import { MarkdownMessage } from "@via/components/markdown/markdown-message";

interface CortexMessageBubbleProps {
  message: {
    role: string;
    content: string;
  };
  index: number;
  isExpanded: boolean;
  isLong: boolean;
  onToggleExpanded: (index: number) => void;
  canAcceptToEditor?: boolean;
  onAcceptToEditor?: (content: string) => void;
}

export const CortexMessageBubble: FC<CortexMessageBubbleProps> = ({
  message,
  index,
  isExpanded,
  isLong,
  onToggleExpanded,
  canAcceptToEditor = false,
  onAcceptToEditor,
}) => {
  const isUser = message.role === "user";
  const isAssistant = message.role === "assistant";
  const isCollapsed = isLong && !isExpanded;
  const shouldShowActions = isAssistant && (canAcceptToEditor || isLong);

  return (
    <div
      key={index}
      className={classNames(
        "relative group max-w-[85%]",
        isUser ? "self-end" : "self-start"
      )}>
      <div
        className={classNames(
          "relative",
          isUser
            ? "p-4 bg-gradient-to-br from-indigo-600 to-indigo-700 text-white rounded-2xl rounded-tr-sm shadow-lg hover:shadow-xl"
            : "p-5 pt-7 bg-gradient-to-br from-indigo-100 to-purple-100 text-gray-800 rounded-2xl rounded-tl-sm shadow-md hover:shadow-lg border border-gray-200/50"
        )}>
        {isAssistant && (
          <div className="absolute top-2 left-3">
            <SparklesIcon className="h-4 w-4 text-indigo-400 opacity-50" />
          </div>
        )}

        {shouldShowActions && (
          <div className="absolute top-2 right-3 flex items-center gap-2">
            {canAcceptToEditor && (
              <button
                type="button"
                onClick={() => onAcceptToEditor?.(message.content)}
                className="inline-flex items-center gap-1 rounded-full bg-white/60 px-2 py-0.5 text-[10px] font-medium text-gray-600 shadow-sm hover:bg-white hover:text-gray-900 transition-colors"
                title="Accept (apply to editor)">
                <CheckIcon className="h-3.5 w-3.5" />
                <span className="text-xs text-gray-500">Accept</span>
              </button>
            )}
            {isLong && (
              <button
                type="button"
                onClick={() => onToggleExpanded(index)}
                className="inline-flex items-center gap-1 rounded-full bg-white/50 px-2 py-0.5 text-[10px] font-medium text-gray-600 shadow-sm hover:bg-white hover:text-gray-900 transition-colors">
                {isCollapsed ? (
                  <>
                    <ChevronDownIcon className="h-3 w-3" />
                    <span className="text-xs text-gray-500">Expand</span>
                  </>
                ) : (
                  <>
                    <ChevronUpIcon className="h-3 w-3" />
                    <span className="text-xs text-gray-500">Collapse</span>
                  </>
                )}
              </button>
            )}
          </div>
        )}

        <div className={isCollapsed ? "max-h-24 overflow-hidden" : ""}>
          <MarkdownMessage
            content={message.content}
            className={classNames("text-sm leading-relaxed font-sans")}
          />
        </div>
      </div>
    </div>
  );
};
